import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import useCallbackReference from '@/core/stateManager/useCallbackReference';
import useSessionStorage from '@/core/stateManager/useSessionStorage';
import { useTracker } from '@/core/tracking';
import { useLastAiToolsChoices } from '@/features/imageEditor/aiToolbar/useLastAiToolsChoices';
import { PaymentSelectionModal } from '@/features/payment/modals';
import { useSubscriptionUpgradePrompts } from '@/features/upgradeSubscription/useSubscriptionUpgradePrompts';
import { usePaymentService } from '@/layout/appWrapper/ServiceProvider';
import { OnboardingModal } from '@/layout/menuActions';
import {
  ImageSelectorMedia,
  useEditorSettings,
} from '@/services/editorService';
import { Format } from '@/services/formatService/formatTypes';
import { TaskType } from '@/services/uploaderService';
import { useUser, UserAction } from '@/services/userService';
import { isLoggedInSelector } from '@/stores/authStore';
import taskToFormatAssociationState from '@/stores/formatState';
import { inputMediaNamesState } from '@/stores/mediaStore';
import { pushModalSelector } from '@/stores/modalStore';
import settingsState from '@/stores/settingsStore';
import type useDownload from '../useDownload';

type useDownloadArg = Pick<
  ReturnType<typeof useDownload>,
  | 'download'
  | 'reprocessIfNeeded'
  | 'needsExportBeforeDownload'
  | 'onExportMedia'
> & {
  media: ImageSelectorMedia;
};

export default function useDownloadTab({
  download,
  media,
  needsExportBeforeDownload,
  onExportMedia,
  reprocessIfNeeded,
}: useDownloadArg) {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const settings = useRecoilValue(settingsState);

  const { track } = useTracker();

  const { isPremiumUser } = usePaymentService();
  const { data: user } = useUser();

  const isLoggedIn = useRecoilValue(isLoggedInSelector);
  const pushModal = useSetRecoilState(pushModalSelector);

  const shouldEnableDownload = useMemo(
    () =>
      (!!user && isPremiumUser(user)) || (user && user.availableCredits! > 0),
    [isPremiumUser, user]
  );

  const [mediaNames, setMediaNames] = useRecoilState(inputMediaNamesState);

  const downloadButtonRef = useRef<HTMLButtonElement>(null);
  const [downloadedTasks, setDownloadedTasks] = useSessionStorage<{
    [key: string]: string[];
  }>('tasks:downloaded', {});

  const [taskToFormatAssociation, setTaskToFormatAssociation] = useRecoilState(
    taskToFormatAssociationState
  );

  const { buildEditorSettingsVisualization } = useEditorSettings();
  const lastAppliedAiToolsChoices = useLastAiToolsChoices(
    settings,
    media.aiPipeline
  );

  const {
    shouldBlockPremiumFormats,
    showUpgradeCTAOnPremiumFormatSelection,
    showUpgradeCTAOnDownloadsLimitReached,
  } = useSubscriptionUpgradePrompts();

  const outputFormat = useMemo(
    () => taskToFormatAssociation[media.id!],
    [media.id, taskToFormatAssociation]
  );

  const hasExhaustedLimits = useMemo(
    () => !!user && (user.imageDownloadsAvailable || 0) <= 0,
    [user]
  );

  const setFileName = useCallback(
    ({ mediaIndex, fileName }: { mediaIndex: number; fileName: string }) => {
      const fileNames = [...mediaNames];
      fileNames[mediaIndex] = fileName;

      setMediaNames(fileNames);
    },
    [mediaNames, setMediaNames]
  );

  const onInputChange = useCallback(
    (mediaIndex: number, event: ChangeEvent<HTMLInputElement>) => {
      setFileName({ mediaIndex, fileName: event.target.value });
    },
    [setFileName]
  );

  const onIndividualDownload = useCallback(async () => {
    setIsLoading(true);

    const fullDownloadResponse = await reprocessIfNeeded();
    const downloadResponse = fullDownloadResponse[media.id!];

    let downloadUrl = downloadResponse.finalOutputUrl;

    if (needsExportBeforeDownload && downloadResponse.hasWatermark) {
      downloadUrl = await onExportMedia(downloadResponse.newTaskId);
    }

    const mediaIndex = media.bulkPosition!;
    const fileName = `${mediaNames[mediaIndex]}.${outputFormat.extension}`;

    await download({
      downloadUrl,
      fileName,
    });
    setIsLoading(false);

    track('user_download', 'triggered', {
      mimeType: outputFormat.mimeType,
      mediaType: TaskType.Image,
      aiPipeline: lastAppliedAiToolsChoices,
    });
  }, [
    download,
    lastAppliedAiToolsChoices,
    media.bulkPosition,
    media.id,
    mediaNames,
    needsExportBeforeDownload,
    onExportMedia,
    outputFormat.extension,
    outputFormat.mimeType,
    reprocessIfNeeded,
    track,
  ]);

  const shouldTrackDownloadTapped = useCallback(
    ({ additionalParams }: { additionalParams?: { [x: string]: any } }) => {
      if (shouldEnableDownload && typeof media.id === 'string') {
        if (
          downloadedTasks[outputFormat.label] &&
          downloadedTasks[outputFormat.label].includes(media.id)
        ) {
          return;
        }

        setDownloadedTasks((prev) => ({
          ...prev,
          [outputFormat.label]: [
            ...(prev[outputFormat.label] || []),
            media.id!,
          ],
        }));
      }

      track('download', 'tapped', {
        mediaType: TaskType.Image,
        taskId: media.id!,
        extension: outputFormat.extension,
        aiPipeline: lastAppliedAiToolsChoices,
        ...additionalParams,
      });
    },
    [
      downloadedTasks,
      lastAppliedAiToolsChoices,
      media.id,
      outputFormat.extension,
      outputFormat.label,
      setDownloadedTasks,
      shouldEnableDownload,
      track,
    ]
  );

  const onOnboardingModalClose = useCallback(async () => {
    downloadButtonRef.current?.focus();

    if (shouldEnableDownload || !media.hasWatermark) {
      await onIndividualDownload();
    }
  }, [media.hasWatermark, onIndividualDownload, shouldEnableDownload]);

  const callOnOnboardingModalClose = useCallbackReference(
    onOnboardingModalClose
  );

  const onDownloadMedia = useCallback(async () => {
    shouldTrackDownloadTapped({
      additionalParams: {
        nextAction: 'user_download',
        mediaType: TaskType.Image,
      },
    });

    if (
      outputFormat.isBusinessOnly &&
      (shouldBlockPremiumFormats || !shouldEnableDownload)
    ) {
      track(`${UserAction.FormatConversion}_cta_popup`, 'displayed', {
        requestedFormat: outputFormat.label,
      });
      showUpgradeCTAOnPremiumFormatSelection();
      return;
    }

    if (
      needsExportBeforeDownload &&
      media.hasWatermark &&
      hasExhaustedLimits &&
      !user?.availableCredits
    ) {
      track(`${UserAction.Download}_cta_popup`, 'displayed');
      await showUpgradeCTAOnDownloadsLimitReached();
      return;
    }

    await onIndividualDownload();
  }, [
    hasExhaustedLimits,
    media.hasWatermark,
    needsExportBeforeDownload,
    onIndividualDownload,
    outputFormat.isBusinessOnly,
    outputFormat.label,
    shouldBlockPremiumFormats,
    shouldEnableDownload,
    shouldTrackDownloadTapped,
    showUpgradeCTAOnDownloadsLimitReached,
    showUpgradeCTAOnPremiumFormatSelection,
    track,
    user?.availableCredits,
  ]);

  const onDownloadClick = useCallback(async () => {
    if (!shouldEnableDownload && !outputFormat.isBusinessOnly) {
      shouldTrackDownloadTapped({
        additionalParams: {
          nextAction: isLoggedIn ? 'paywall' : 'login',
          mediaType: TaskType.Image,
        },
      });

      if (
        settings.areReady &&
        (settings.values.oneTimePayment ||
          settings.values.businessTierWeeklyHighlighted ||
          !settings.values.shouldSkipPaymentHighlightPopup) &&
        media.hasWatermark &&
        !settings.values.shouldSkipPaymentHighlightPopup
      ) {
        track(
          settings.values.businessTierWeeklyHighlighted
            ? 'business_weekly_popup'
            : 'one_time_popup',
          'shown'
        );
        pushModal({
          id: 'payment-selection',
          Modal: PaymentSelectionModal,
          previousAction: UserAction.Download,
          onClose: callOnOnboardingModalClose,
        });
      } else if (media.hasWatermark) {
        pushModal({
          id: 'page:compare:before-download',
          Modal: OnboardingModal,
          previousAction: UserAction.Download,
          onClose: callOnOnboardingModalClose,
        });
      } else {
        await onDownloadMedia();
      }
    } else {
      await onDownloadMedia();
    }
  }, [
    callOnOnboardingModalClose,
    isLoggedIn,
    media.hasWatermark,
    onDownloadMedia,
    outputFormat.isBusinessOnly,
    pushModal,
    settings.areReady,
    settings.values.businessTierWeeklyHighlighted,
    settings.values.oneTimePayment,
    shouldEnableDownload,
    shouldTrackDownloadTapped,
    track,
  ]);

  const onFormatSelection = useCallback(
    (format: Format) => {
      setTaskToFormatAssociation((prev) => ({
        ...prev,
        [media.id]: format,
      }));
    },
    [media.id, setTaskToFormatAssociation]
  );

  const mediaPositionInRemoteResponse = useMemo(
    () => media.bulkPosition! || 0,
    [media.bulkPosition]
  );

  return {
    aiToolsChoices: lastAppliedAiToolsChoices,
    aiToolbarElements: buildEditorSettingsVisualization(),
    downloadButtonRef,
    isLoading,
    mediaNames,
    mediaPositionInRemoteResponse,
    onDownloadClick,
    onFormatSelection,
    onInputChange,
    taskToFormatAssociation,
  };
}
